import { getCohortStatus } from 'utils/cohortUtils.ts';
import useReferenceData from '../../../hooks/useReferenceData.ts';
import { IterationRule } from '../../../schemas/IterationRule.schema.ts';
import { capitaliseFirstChar, deriveDisplayName } from '../../../utils/formattingUtils.ts';
import { isNewStyleRule, IterationRuleWithAndClauses } from '../../../utils/ruleUtils.ts';
import RuleRowListValues from './RuleRowListValues.tsx';
import RuleRowPickListValues from './RuleRowPickListValues.tsx';

interface RuleAttributesProps {
  rule: IterationRule;
  isAndClause?: boolean;
  isDefaultRule?: boolean;
}

export default function RuleAttributes({ rule, isAndClause = false, isDefaultRule = false }: RuleAttributesProps) {
  const className = `rule-attributes ${isAndClause ? 'and-clause-attributes' : ''}`;
  const { referenceData } = useReferenceData();
  const cohortsData = referenceData.cohorts;
  const operatorData = referenceData.operators;

  const comparatorValues = rule.Comparator?.split(',') ?? [];

  const addCustomText = () => {
    if (rule.AttributeLevel === 'COHORT' && rule.Comparator && comparatorValues.length === 1) {
      const cohortStatus = getCohortStatus(rule.Comparator as string, cohortsData);
      if (cohortStatus !== 'ok') {
        return ` (${capitaliseFirstChar(cohortStatus)})`;
      }
    }

    return '';
  };

  const getOperatorDisplayName = (operator: string | undefined) => {
    const operatorEntry = operatorData.find((op) => op.Operator === operator);
    return operatorEntry ? operatorEntry.DisplayValue.toLowerCase() : operator?.toLowerCase();
  };

  const computeBaseComparator = () => {
    if (rule.AttributeLevel === 'COHORT' && rule.Comparator && comparatorValues.length > 1) {
      return comparatorValues
        .map((value) => {
          const trimmedValue = value.trim();
          const cohortStatus = getCohortStatus(trimmedValue, cohortsData);
          if (cohortStatus === 'ok') {
            return trimmedValue;
          }

          return `${trimmedValue} (${capitaliseFirstChar(cohortStatus)})`;
        })
        .join(',');
    }

    return rule.Comparator || '';
  };

  const getFormattedComparator = () => {
    const baseComparator = computeBaseComparator();

    // Attempting to ensure wrapping works on longer comparators
    if (baseComparator.length > 50) {
      return baseComparator.replace(/,/g, ', ');
    }

    return baseComparator;
  };

  const getAttributeDescription = (r: IterationRuleWithAndClauses) => {
    if (isAndClause && r.DisplayName && isNewStyleRule(r)) {
      return r.DisplayName;
    }

    if (r.DisplayDescription) {
      return r.DisplayDescription;
    }

    if (r.Name && isDefaultRule) {
      return r.Name;
    }

    return `${deriveDisplayName(r.AttributeName ?? '')} ${getOperatorDisplayName(r.Operator)} ${getFormattedComparator()}`;
  };

  return (
    <div className={className}>
      {rule.AttributeTarget !== 'ALL' && !isNewStyleRule(rule)
      && (
        <>
          <i className="rule-target">{rule.AttributeTarget}</i>
          &nbsp;
        </>
      )}
      {getAttributeDescription(rule)}
      {' '}
      {rule.LibraryRuleMetadata?.Type === 'PICK_LIST' && (
        <RuleRowPickListValues rule={rule} />
      )}
      {rule.LibraryRuleMetadata?.Type === 'LIST' && (
        <RuleRowListValues rule={rule} />
      )}

      {addCustomText()}
    </div>
  );
}
